<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFetch } from '@/composables/useFetch';
import FilterDrawer from '@/components/FilterDrawer.vue';
import { onContentChanged } from '@/composables/useContentRefresh';

const { t } = useI18n();
const ofetch = useFetch();

// Types
interface ListItem {
  id: number;
  title: string;
  type: string;
  url: string;
  image_url: string | null;
  summary: string;
  moderation_state?: string;
}

interface Facet {
  field: string;
  label: string;
  options: {
    id: number | string;
    label: string;
    selected: boolean;
  }[];
}

interface ListBlockData {
  items: ListItem[];
  total: number;
  total_unfiltered: number;
  page: number;
  page_size: number;
  total_pages: number;
  facets: Facet[];
  list_type: string;
  context: Record<string, string>;
  error?: string;
}

// Props from drupalSettings
const settings = (window as any).drupalSettings?.epbListBlock || {};
const nodeFiltersFromSettings = (window as any).drupalSettings?.epbNodeFacets?.filters || null;

const apiUrl = settings.apiUrl || '/epbapi/list-block';
const listType = ref<string>(settings.listType || 'nwrm');
const displayLayout = ref<string>(settings.displayLayout || settings.listType || 'nwrm');
const pageSize = ref<number>(settings.pageSize || 12);

// State
const loading = ref(true);
const error = ref<string | null>(null);
const blockData = ref<ListBlockData | null>(settings.initialData || null);
const activeFilters = ref<Record<string, (number | string)[]>>({});
const filterLogic = ref<Record<string, 'and' | 'or'>>({});
const globalFilterLogic = ref<'and' | 'or'>('and');
const currentPage = ref(0);
const showFilters = ref(false);

// Computed
const items = computed(() => blockData.value?.items || []);
const totalItems = computed(() => blockData.value?.total || 0);
const totalUnfiltered = computed(() => blockData.value?.total_unfiltered ?? totalItems.value);
const totalPages = computed(() => blockData.value?.total_pages || 0);
const facets = computed(() => blockData.value?.facets || []);

// Check if any filter is active
const hasActiveFilters = computed(() => {
  return Object.values(activeFilters.value).some(values => values.length > 0);
});

// Grid class based on display layout (uses displayLayout for visual, listType for API filter)
const gridClass = computed(() => {
  switch (displayLayout.value) {
    case 'cs':
      return 'row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4';
    case 'nswrm':
      return 'row row-cols-1 row-cols-md-2 g-4';
    case 'nwrm':
    default:
      return 'list-group list-group-flush';
  }
});

// Is list layout (horizontal cards) vs grid layout
const isListLayout = computed(() => displayLayout.value === 'nwrm');

// Load data from API
async function loadData() {
  loading.value = true;
  error.value = null;

  try {
    const params = new URLSearchParams();
    params.append('list_type', listType.value);
    params.append('page', String(currentPage.value));
    params.append('page_size', String(pageSize.value));

    if (hasActiveFilters.value) {
      params.append('filters', JSON.stringify(activeFilters.value));
      const hasCustomLogic = Object.values(filterLogic.value).some(v => v === 'and');
      if (hasCustomLogic) {
        params.append('filter_logic', JSON.stringify(filterLogic.value));
      }
      if (globalFilterLogic.value === 'or') {
        params.append('global_logic', 'or');
      }
    }

    // Pass current context from URL (including gid for group filtering)
    const urlParams = new URLSearchParams(window.location.search);
    ['fc', 'fp', 'fa', 'gid', 'filter_collection', 'filter_pathway', 'filter_area', 'ids'].forEach(key => {
      const value = urlParams.get(key);
      if (value) params.append(key, value);
    });

    const url = `${apiUrl}?${params.toString()}`;
    const data = await ofetch<ListBlockData>(url);

    blockData.value = data;

    // Update current page from server response
    if (typeof data.page === 'number') {
      currentPage.value = data.page;
    }
  } catch (e: any) {
    error.value = e.message || 'Failed to load data';
    console.error('ListBlock load error:', e);
  } finally {
    loading.value = false;
  }
}

// Toggle filters drawer
function toggleFilters() {
  showFilters.value = !showFilters.value;
}

// Toggle filter value
function toggleFilter(field: string, valueId: number | string) {
  if (!activeFilters.value[field]) {
    activeFilters.value[field] = [];
  }

  const index = activeFilters.value[field].indexOf(valueId);
  if (index === -1) {
    activeFilters.value[field].push(valueId);
  } else {
    activeFilters.value[field].splice(index, 1);
  }

  // Reset to first page and reload
  currentPage.value = 0;
  loadData();
  dispatchFilterEvent();
}

// Toggle AND/OR logic for a specific facet
function toggleFacetLogic(field: string) {
  const current = filterLogic.value[field] || 'or';
  filterLogic.value[field] = current === 'or' ? 'and' : 'or';
  if (hasActiveFilters.value) {
    currentPage.value = 0;
    loadData();
  }
  dispatchFilterEvent();
}

// Toggle global AND/OR logic between categories
function toggleGlobalLogic() {
  globalFilterLogic.value = globalFilterLogic.value === 'and' ? 'or' : 'and';
  if (hasActiveFilters.value) {
    currentPage.value = 0;
    loadData();
  }
  dispatchFilterEvent();
}

// Dispatch filter event for cross-block sync
function dispatchFilterEvent() {
  window.dispatchEvent(new CustomEvent('epb:filters-changed', {
    detail: {
      filters: activeFilters.value,
      filter_logic: filterLogic.value,
      global_logic: globalFilterLogic.value,
      source: 'listblock',
    }
  }));
}

// Clear all filters
function clearFilters() {
  activeFilters.value = {};
  filterLogic.value = {};
  globalFilterLogic.value = 'and';
  currentPage.value = 0;
  loadData();

  window.dispatchEvent(new CustomEvent('epb:filters-changed', {
    detail: { filters: {}, filter_logic: {}, global_logic: 'and', source: 'listblock' }
  }));
}

// Apply current node's facet values as active filters
function applyNodeFilters(filters: Record<string, (number | string)[]>) {
  activeFilters.value = { ...filters };
  currentPage.value = 0;
  loadData();
  dispatchFilterEvent();
}

// Pagination
function goToPage(page: number) {
  if (page >= 0 && page < totalPages.value) {
    currentPage.value = page;
    loadData();
  }
}

// Listen for filter changes from other blocks
function onFiltersChanged(event: CustomEvent) {
  if (event.detail.source !== 'listblock') {
    activeFilters.value = event.detail.filters || {};
    if (event.detail.filter_logic) {
      filterLogic.value = event.detail.filter_logic;
    }
    if (event.detail.global_logic) {
      globalFilterLogic.value = event.detail.global_logic;
    }
    currentPage.value = 0;
    loadData();
  }
}

// Re-fetch in place when another navigation block changes content.
onContentChanged('listblock', () => loadData());

// Mount
onMounted(() => {
  // Listen for filter sync events
  window.addEventListener('epb:filters-changed', onFiltersChanged as EventListener);

  // Load data if not pre-loaded
  if (!blockData.value || !blockData.value.items) {
    loadData();
  } else {
    loading.value = false;
  }
});
</script>

<template>
  <div class="list-block-container">
    <!-- Header with filter button -->
    <div class="list-block-header d-flex align-items-center gap-2 mb-3">
      <button
        class="header-icon-btn"
        :class="{ 'active': showFilters || hasActiveFilters, 'disabled': facets.length === 0 }"
        :disabled="facets.length === 0"
        @click="toggleFilters"
        type="button"
        :title="facets.length > 0 ? 'Filter content' : 'No filters available'"
      >
        <i class="bi bi-funnel"></i>
        <span v-if="hasActiveFilters" class="filter-badge"></span>
      </button>
      <small v-if="totalUnfiltered > 0 && !loading" :class="hasActiveFilters ? 'text-primary fw-semibold' : 'text-secondary'">
        <template v-if="hasActiveFilters">{{ totalItems }}/</template>{{ totalUnfiltered }}
      </small>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else>
      <!-- No results -->
      <div v-if="items.length === 0" class="text-center text-muted p-5">
        <i class="bi bi-inbox fs-1 d-block mb-3"></i>
        {{ t('epb.list.no_results') }}
      </div>

      <!-- List layout (NWRM) -->
      <div v-else-if="isListLayout" :class="gridClass">
        <div
          v-for="item in items"
          :key="item.id"
          class="list-group-item list-group-item-action p-0 border-0 mb-3"
        >
          <div class="card card-horizontal">
            <div class="row g-0">
              <div class="col-md-3" v-if="item.image_url">
                <a :href="item.url">
                  <img
                    :src="item.image_url"
                    :alt="item.title"
                    class="img-fluid rounded-start card-img-left"
                  />
                </a>
              </div>
              <div :class="item.image_url ? 'col-md-9' : 'col-12'">
                <div class="card-body">
                  <h5 class="card-title">
                    <a :href="item.url" class="text-decoration-none">
                      {{ item.title }}
                    </a>
                  </h5>
                  <p class="card-text text-muted" v-if="item.summary">
                    {{ item.summary }}
                  </p>
                  <a :href="item.url" class="btn btn-primary btn-sm">
                    {{ t('epb.list.read_more') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Grid layout (CS, NSWRM) -->
      <div v-else :class="gridClass">
        <div v-for="item in items" :key="item.id" class="col">
          <div class="card h-100">
            <a :href="item.url" v-if="item.image_url">
              <img
                :src="item.image_url"
                :alt="item.title"
                class="card-img-top"
              />
            </a>
            <div class="card-body d-flex flex-column">
              <h5 class="card-title">
                <a :href="item.url" class="text-decoration-none">
                  {{ item.title }}
                </a>
              </h5>
              <p class="card-text text-muted flex-grow-1" v-if="item.summary">
                {{ item.summary }}
              </p>
              <a :href="item.url" class="btn btn-primary btn-sm align-self-start mt-auto">
                {{ t('epb.list.more') }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <nav v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: currentPage === 0 }">
            <button class="page-link" @click="goToPage(currentPage - 1)" type="button">
              <i class="bi bi-chevron-left"></i>
            </button>
          </li>
          <li
            v-for="page in totalPages"
            :key="page"
            class="page-item"
            :class="{ active: currentPage === page - 1 }"
          >
            <button class="page-link" @click="goToPage(page - 1)" type="button">
              {{ page }}
            </button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage >= totalPages - 1 }">
            <button class="page-link" @click="goToPage(currentPage + 1)" type="button">
              <i class="bi bi-chevron-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>

  <FilterDrawer
    :show="showFilters"
    :facets="facets"
    :active-filters="activeFilters"
    :filter-logic="filterLogic"
    :global-filter-logic="globalFilterLogic"
    :node-filters="nodeFiltersFromSettings"
    @update:show="showFilters = $event"
    @toggle-filter="toggleFilter"
    @toggle-facet-logic="toggleFacetLogic"
    @toggle-global-logic="toggleGlobalLogic"
    @clear-filters="clearFilters"
    @apply-node-filters="applyNodeFilters"
  />
</template>

<style scoped>
.list-block-container {
  width: 100%;
}

/* Filter icon button (same as AreasBlock) */
.header-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background-color: var(--bs-gray-200);
  color: var(--bs-body-color);
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}
.header-icon-btn:hover { background-color: var(--bs-primary); color: white; }
.header-icon-btn.active { background-color: var(--bs-warning); color: var(--bs-dark); }
.header-icon-btn.active:hover { background-color: var(--bs-warning); color: var(--bs-dark); }
.header-icon-btn:disabled,
.header-icon-btn.disabled { opacity: 0.5; cursor: not-allowed; }
.header-icon-btn:disabled:hover,
.header-icon-btn.disabled:hover { background-color: var(--bs-gray-200); color: var(--bs-body-color); }

.filter-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  background-color: var(--bs-primary);
  border-radius: 50%;
}

/* Horizontal card for list layout */
.card-horizontal {
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  overflow: hidden;
}

.card-horizontal .card-img-left {
  height: 100%;
  object-fit: cover;
  min-height: 150px;
  max-height: 200px;
}

/* Grid cards */
.card {
  transition: box-shadow 0.2s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-img-top {
  height: 180px;
  object-fit: cover;
}

.card-title a {
  color: var(--bs-body-color);
}

.card-title a:hover {
  color: var(--bs-primary);
}

.card-text {
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Pagination */
.pagination {
  margin-bottom: 0;
}

.page-link {
  cursor: pointer;
}
</style>

